import React from 'react';
import { motion } from 'framer-motion';
import { FiX, FiZoomIn, FiZoomOut } from 'react-icons/fi';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  alt: string;
  title?: string;
}

const ImageModal: React.FC<ImageModalProps> = ({ isOpen, onClose, src, alt, title }) => {
  const [zoom, setZoom] = React.useState(1);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  // Reset zoom and position when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (zoom > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && zoom > 1 && e.touches.length === 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Modal content */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="relative max-w-[95vw] max-h-[95vh] bg-light-surface dark:bg-dark-surface rounded-lg shadow-2xl overflow-hidden border border-light-border dark:border-dark-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
          <div className="flex-1">
            {title && (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {title}
              </h3>
            )}
          </div>
          
          {/* Zoom controls */}
          <div className="flex items-center gap-2 mr-4">
            <button
              onClick={handleZoomOut}
              disabled={zoom <= 0.5}
              className="p-2 rounded-md bg-light-hover dark:bg-dark-hover hover:bg-gray-200 dark:hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-light-border dark:border-dark-border"
              aria-label="Zoom out"
            >
              <FiZoomOut className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            
            <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3rem] text-center font-medium">
              {Math.round(zoom * 100)}%
            </span>
            
            <button
              onClick={handleZoomIn}
              disabled={zoom >= 3}
              className="p-2 rounded-md bg-light-hover dark:bg-dark-hover hover:bg-gray-200 dark:hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-light-border dark:border-dark-border"
              aria-label="Zoom in"
            >
              <FiZoomIn className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-2 rounded-md bg-light-hover dark:bg-dark-hover hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors border border-light-border dark:border-dark-border hover:border-red-300 dark:hover:border-red-700"
            aria-label="Close modal"
          >
            <FiX className="w-5 h-5 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors" />
          </button>
        </div>

        {/* Image container */}
        <div 
          className="relative overflow-hidden bg-light-surface dark:bg-dark-surface"
          style={{ 
            width: 'min(90vw, 800px)', 
            height: 'min(80vh, 600px)',
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <motion.img
            src={src}
            alt={alt}
            className="w-full h-full object-contain select-none"
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              transformOrigin: 'center center'
            }}
            transition={{ type: 'tween', duration: isDragging ? 0 : 0.2 }}
            draggable={false}
          />
        </div>

        {/* Instructions */}
        <div className="p-3 bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {zoom > 1 ? 'Drag to pan • ' : ''}Use zoom controls or scroll to zoom • Press ESC to close
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ImageModal;
